import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

function PanelShell({ title, actions, children, testId }) {
  return (
    <div
      className="rounded-xl border border-white/10 bg-black/20 p-3 space-y-2 text-xs"
      data-testid={testId}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold text-zinc-300">{title}</h3>
        {actions}
      </div>
      {children}
    </div>
  );
}

async function copyText(text, label = 'text') {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`Copied ${label}`);
  } catch {
    toast.error('Copy failed');
  }
}

export function InstructionsPanel({ agentId, backendUrl, authHeaders }) {
  const [instructions, setInstructions] = useState(null);
  const [bindings, setBindings] = useState(null);
  const [purpose, setPurpose] = useState('');
  const [doneWhen, setDoneWhen] = useState('');
  const [stepsText, setStepsText] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = useCallback(() => {
    if (!agentId) return;
    setLoading(true);
    fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/instructions`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => {
        const inst = d.instructions || {};
        setInstructions(inst);
        setBindings(d.bindings || null);
        setPurpose(inst.purpose || '');
        setDoneWhen(inst.definition_of_done || '');
        setStepsText((inst.workflow_steps || []).join('\n'));
      })
      .catch(() => toast.error('Failed to load instructions'))
      .finally(() => setLoading(false));
  }, [agentId, backendUrl, authHeaders]);

  useEffect(load, [load]);

  const save = async () => {
    if (!agentId) return;
    setSaving(true);
    const next = {
      ...(instructions || {}),
      purpose: purpose.trim(),
      definition_of_done: doneWhen.trim(),
      workflow_steps: stepsText.split('\n').map((s) => s.trim()).filter(Boolean),
    };
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/instructions`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ instructions: next }),
      });
      if (!res.ok) throw new Error('Save failed');
      setInstructions(next);
      setEditing(false);
      toast.success('Instructions saved');
    } catch {
      toast.error('Failed to save instructions');
    } finally {
      setSaving(false);
    }
  };

  if (!agentId) return null;

  return (
    <PanelShell
      title="Instructions"
      testId="agent-instructions-panel"
      actions={(
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-[10px]"
          onClick={() => (editing ? save() : setEditing(true))}
          disabled={loading || saving}
        >
          {editing ? (saving ? 'Saving…' : 'Save') : 'Edit'}
        </Button>
      )}
    >
      {loading && <p className="text-zinc-500">Loading…</p>}
      {!loading && editing && (
        <div className="space-y-2">
          <div>
            <label className="text-zinc-500 block mb-0.5">Purpose</label>
            <textarea
              className="w-full min-h-[60px] rounded-lg bg-zinc-900 border border-white/10 px-2 py-1 text-zinc-200 text-xs"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
            />
          </div>
          <div>
            <label className="text-zinc-500 block mb-0.5">Workflow steps (one per line)</label>
            <textarea
              className="w-full min-h-[72px] rounded-lg bg-zinc-900 border border-white/10 px-2 py-1 text-zinc-200 text-xs"
              value={stepsText}
              onChange={(e) => setStepsText(e.target.value)}
            />
          </div>
          <div>
            <label className="text-zinc-500 block mb-0.5">Definition of done</label>
            <textarea
              className="w-full min-h-[48px] rounded-lg bg-zinc-900 border border-white/10 px-2 py-1 text-zinc-200 text-xs"
              value={doneWhen}
              onChange={(e) => setDoneWhen(e.target.value)}
            />
          </div>
          <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </div>
      )}
      {!loading && !editing && instructions && (
        <div className="space-y-1">
          <p className="text-zinc-400"><span className="text-zinc-500">Purpose:</span> {instructions.purpose}</p>
          {(instructions.workflow_steps || []).map((s, i) => (
            <p key={i} className="text-zinc-400">{i + 1}. {s}</p>
          ))}
          {instructions.definition_of_done && (
            <p className="text-zinc-500">Done when: {instructions.definition_of_done}</p>
          )}
          {bindings?.run_mode && (
            <p className="text-zinc-600">Mode: {bindings.run_mode}</p>
          )}
        </div>
      )}
    </PanelShell>
  );
}

export function KnowledgeAttachPanel({
  agentId, backendUrl, authHeaders, knowledgeBases, onLinked, onReloadKnowledgeBases,
}) {
  const [linked, setLinked] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newKbName, setNewKbName] = useState('');
  const [ingestUrl, setIngestUrl] = useState({});
  const [ingestMaxPages, setIngestMaxPages] = useState({});
  const [ingesting, setIngesting] = useState(null);
  const [pasteKbId, setPasteKbId] = useState(null);
  const [pasteTitle, setPasteTitle] = useState('');
  const [pasteContent, setPasteContent] = useState('');
  const [pasting, setPasting] = useState(false);

  const loadLinked = useCallback(() => {
    if (!agentId) return;
    fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/instructions`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => setLinked(d.bindings?.knowledge_base_ids || []))
      .catch(() => {});
  }, [agentId, backendUrl, authHeaders]);

  useEffect(loadLinked, [loadLinked]);

  const toggleKb = async (kbId) => {
    const isLinked = linked.includes(kbId);
    const res = await fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/knowledge`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ link: isLinked ? [] : [kbId], unlink: isLinked ? [kbId] : [] }),
    });
    if (!res.ok) {
      toast.error('Failed to update knowledge');
      return;
    }
    const data = await res.json();
    setLinked(data.knowledge_base_ids || []);
    onLinked?.(data.knowledge_base_ids);
    toast.success(isLinked ? 'Knowledge unlinked' : 'Knowledge linked');
  };

  const createKb = async () => {
    const name = newKbName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/knowledge-bases`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Create failed');
      toast.success('Knowledge base created');
      setNewKbName('');
      onReloadKnowledgeBases?.();
      await toggleKb(data.id);
    } catch (err) {
      toast.error(err.message || 'Failed to create');
    } finally {
      setCreating(false);
    }
  };

  const ingestUrlForKb = async (kbId) => {
    const url = (ingestUrl[kbId] || '').trim();
    if (!url) return;
    setIngesting(kbId);
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/knowledge-bases/${kbId}/ingest-url`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url,
          use_browser: true,
          max_pages: Math.min(30, Math.max(1, parseInt(ingestMaxPages[kbId], 10) || 10)),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data.detail;
        throw new Error(
          typeof detail === 'string' ? detail : detail?.message || data.error || 'Ingest failed',
        );
      }
      if (data.status === 'failed') {
        throw new Error(data.error || 'Ingest failed');
      }
      if (!(data.chunk_count > 0)) {
        throw new Error(
          'No text was extracted from that URL. Try a direct policy/help page or paste text below.',
        );
      }
      const method = data.fetch_method || 'browser';
      const pages = data.pages_fetched ? ` · ${data.pages_fetched} page(s)` : '';
      toast.success(`Ingested ${data.chunk_count} chunk(s) via ${method}${pages}`);
      setIngestUrl((prev) => ({ ...prev, [kbId]: '' }));
      onReloadKnowledgeBases?.();
    } catch (err) {
      toast.error(err.message || 'Ingest failed');
    } finally {
      setIngesting(null);
    }
  };

  const addTextDoc = async (kbId) => {
    const title = pasteTitle.trim();
    const content = pasteContent.trim();
    if (!content) {
      toast.error('Paste some text first');
      return;
    }
    setPasting(true);
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/knowledge-bases/${kbId}/documents`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, content }),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data.detail;
        throw new Error(typeof detail === 'string' ? detail : detail?.message || 'Failed');
      }
      toast.success(`Added ${data.chunk_count} chunk(s)`);
      setPasteTitle('');
      setPasteContent('');
      setPasteKbId(null);
      onReloadKnowledgeBases?.();
    } catch (err) {
      toast.error(err.message || 'Failed to add document');
    } finally {
      setPasting(false);
    }
  };

  if (!agentId) return null;

  return (
    <PanelShell title="Knowledge" testId="agent-knowledge-panel">
      <div className="flex gap-1">
        <Input
          value={newKbName}
          onChange={(e) => setNewKbName(e.target.value)}
          placeholder="New knowledge base name"
          className="h-7 text-[11px] flex-1"
          onKeyDown={(e) => e.key === 'Enter' && createKb()}
        />
        <Button size="sm" variant="outline" className="h-7 text-[10px] shrink-0" onClick={createKb} disabled={creating}>
          + New
        </Button>
      </div>
      {!knowledgeBases?.length && (
        <p className="text-zinc-500">No knowledge bases yet — create one above to attach docs and URLs.</p>
      )}
      {knowledgeBases?.map((k) => (
        <div key={k.id} className="border border-white/5 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => toggleKb(k.id)}
            className={`w-full text-left px-2 py-1.5 flex items-center justify-between ${
              linked.includes(k.id) ? 'bg-emerald-500/10' : ''
            }`}
          >
            <span className="text-zinc-300">{k.name}</span>
            <span className="text-zinc-500">{linked.includes(k.id) ? '✓ linked' : 'link'}</span>
          </button>
          {linked.includes(k.id) && (
            <div className="px-2 pb-2 space-y-1 border-t border-white/5 pt-1">
              <div className="flex gap-1">
                <Input
                  value={ingestUrl[k.id] || ''}
                  onChange={(e) => setIngestUrl((prev) => ({ ...prev, [k.id]: e.target.value }))}
                  placeholder="https://yoursite.com (main URL)"
                  className="h-7 text-[10px] flex-1"
                />
                <Input
                  value={ingestMaxPages[k.id] ?? '10'}
                  onChange={(e) => setIngestMaxPages((prev) => ({ ...prev, [k.id]: e.target.value }))}
                  placeholder="Pages"
                  title="Max same-site pages to crawl (browser)"
                  className="h-7 text-[10px] w-14"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[10px]"
                  disabled={ingesting === k.id}
                  onClick={() => ingestUrlForKb(k.id)}
                >
                  {ingesting === k.id ? '…' : 'Ingest'}
                </Button>
              </div>
              <p className="text-[10px] text-zinc-600">
                Browser crawl (Chromium) — follows same-site links. Use a policy/help URL, not just a homepage.
              </p>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 text-[10px] px-1"
                onClick={() => {
                  setPasteKbId(pasteKbId === k.id ? null : k.id);
                  setPasteTitle('');
                  setPasteContent('');
                }}
              >
                {pasteKbId === k.id ? '− Hide paste' : '+ Paste text (fallback)'}
              </Button>
              {pasteKbId === k.id && (
                <div className="space-y-1 pt-1">
                  <Input
                    value={pasteTitle}
                    onChange={(e) => setPasteTitle(e.target.value)}
                    placeholder="Title (optional)"
                    className="h-7 text-[10px]"
                  />
                  <Textarea
                    value={pasteContent}
                    onChange={(e) => setPasteContent(e.target.value)}
                    placeholder="Paste refund policy or FAQ text here…"
                    className="text-[10px] min-h-[88px] bg-black/30"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-[10px]"
                    disabled={pasting}
                    onClick={() => addTextDoc(k.id)}
                  >
                    {pasting ? 'Saving…' : 'Save text to knowledge base'}
                  </Button>
                </div>
              )}
              {k.chunk_count != null && (
                <p className="text-[10px] text-zinc-600">{k.chunk_count} chunk(s)</p>
              )}
            </div>
          )}
        </div>
      ))}
    </PanelShell>
  );
}

export function McpToolsPanel({ agentId, backendUrl, authHeaders }) {
  const [servers, setServers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!agentId) return;
    Promise.all([
      fetch(`${backendUrl}/api/ai-studio/mcp/servers`, { headers: authHeaders() }).then((r) => r.json()),
      fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/instructions`, { headers: authHeaders() }).then((r) => r.json()),
    ])
      .then(([srv, inst]) => {
        setServers(srv.servers || []);
        setSelected(inst.bindings?.mcp_server_ids || ['fetch', 'memory']);
      })
      .catch(() => toast.error('Failed to load MCP servers'));
  }, [agentId, backendUrl, authHeaders]);

  useEffect(load, [load]);

  const toggle = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const save = async () => {
    if (!agentId || selected.length === 0) {
      toast.error('Select at least one MCP server');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/bindings`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ mcp_server_ids: selected }),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success('MCP tools updated');
    } catch {
      toast.error('Failed to save MCP settings');
    } finally {
      setSaving(false);
    }
  };

  if (!agentId) return null;

  return (
    <PanelShell
      title="MCP tools"
      testId="agent-mcp-panel"
      actions={(
        <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      )}
    >
      <p className="text-zinc-500">Built-in Model Context Protocol servers available to this agent.</p>
      {servers.length === 0 && <p className="text-zinc-600">No MCP servers registered.</p>}
      {servers.map((s) => (
        <label key={s.name} className="flex items-start gap-2 cursor-pointer py-0.5">
          <input
            type="checkbox"
            checked={selected.includes(s.name)}
            onChange={() => toggle(s.name)}
            className="mt-0.5"
          />
          <span>
            <span className="text-zinc-300 font-medium">{s.label || s.name}</span>
            {s.description && <span className="block text-[10px] text-zinc-500">{s.description}</span>}
          </span>
        </label>
      ))}
    </PanelShell>
  );
}

export function TriggersPanel({ agentId, backendUrl, authHeaders }) {
  const [triggers, setTriggers] = useState([]);

  const load = useCallback(() => {
    if (!agentId) return;
    fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/triggers`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => setTriggers(d.triggers || []))
      .catch(() => {});
  }, [agentId, backendUrl, authHeaders]);

  useEffect(load, [load]);

  const addWebhook = async () => {
    const res = await fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/triggers`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'webhook', config: { prompt: 'Process webhook payload' } }),
    });
    if (res.ok) {
      toast.success('Webhook trigger created');
      load();
    } else {
      toast.error('Failed to create webhook');
    }
  };

  const addDailySchedule = async () => {
    const res = await fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/triggers`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'schedule',
        config: { cron: '0 9 * * *', timezone: 'UTC', label: 'Daily at 9:00 UTC', prompt: 'Run daily automation' },
      }),
    });
    if (res.ok) {
      toast.success('Daily schedule created (9:00 UTC)');
      load();
    } else {
      toast.error('Failed to create schedule');
    }
  };

  const addGmailEvent = async () => {
    const res = await fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/triggers`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'event',
        config: {
          provider: 'gmail',
          event: 'new_email',
          poll_minutes: 5,
          label: 'On new Gmail message',
          prompt: 'Process the new email and summarize or take action as configured.',
        },
      }),
    });
    if (res.ok) {
      toast.success('Gmail event trigger created (polls every 5 min)');
      load();
    } else {
      toast.error('Failed to create Gmail trigger');
    }
  };

  const removeTrigger = async (triggerId) => {
    const res = await fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/triggers/${triggerId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (res.ok) {
      toast.success('Trigger removed');
      load();
    } else {
      toast.error('Failed to remove trigger');
    }
  };

  if (!agentId) return null;

  const webhookUrl = (t) => `${backendUrl}/api/ai-studio/webhooks/agent/${t.id}`;

  return (
    <PanelShell
      title="Triggers"
      testId="agent-triggers-panel"
      actions={(
        <div className="flex gap-1">
          <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={addDailySchedule}>
            + Daily 9am
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={addGmailEvent}>
            + Gmail
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={addWebhook}>
            + Webhook
          </Button>
        </div>
      )}
    >
      {triggers.length === 0 && (
        <p className="text-zinc-500">No triggers — webhooks fire instantly; Gmail polls every ~5 min.</p>
      )}
      {triggers.map((t) => (
        <div key={t.id} className="border border-white/5 rounded-lg px-2 py-1.5 space-y-1">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-zinc-300 capitalize">{t.type}</span>
            <Button size="sm" variant="ghost" className="h-6 text-[10px] text-red-400" onClick={() => removeTrigger(t.id)}>
              Delete
            </Button>
          </div>
          {t.type === 'webhook' && (
            <>
              <p className="text-[10px] text-emerald-400/80">Instant — POST any JSON payload</p>
              <code className="block text-[10px] text-zinc-500 break-all">{webhookUrl(t)}</code>
              <div className="flex flex-wrap gap-1">
                <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => copyText(webhookUrl(t), 'webhook URL')}>
                  Copy URL
                </Button>
                {t.config?.secret && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 text-[10px]"
                    onClick={() => copyText(t.config.secret, 'webhook secret')}
                  >
                    Copy secret
                  </Button>
                )}
              </div>
              <p className="text-[10px] text-zinc-600">Header: X-Webhook-Secret</p>
            </>
          )}
          {t.type === 'schedule' && (
            <p className="text-[10px] text-zinc-500">{t.config?.label || t.config?.cron}</p>
          )}
          {t.type === 'event' && (
            <p className="text-[10px] text-zinc-500">
              {t.config?.label || `${t.config?.provider || 'event'}: ${t.config?.event || ''}`}
              {t.config?.poll_minutes ? ` · polls every ${t.config.poll_minutes}m` : ''}
            </p>
          )}
        </div>
      ))}
    </PanelShell>
  );
}

export function DeployPanel({
  agentId, backendUrl, authHeaders, onGenerateApiKey, onEnableWidget,
}) {
  const [deployments, setDeployments] = useState([]);
  const [newApiKey, setNewApiKey] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!agentId) return;
    fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/deployments`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => setDeployments(d.deployments || []))
      .catch(() => {});
  }, [agentId, backendUrl, authHeaders]);

  useEffect(load, [load]);

  const refresh = () => {
    setLoading(true);
    load();
    setTimeout(() => setLoading(false), 400);
  };

  const generateKey = async () => {
    if (onGenerateApiKey) {
      const key = await onGenerateApiKey(agentId);
      if (key) setNewApiKey(key);
      refresh();
      return;
    }
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/api-key`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setNewApiKey(data);
      toast.success('API key generated');
      refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to generate key');
    }
  };

  const enableWidget = async () => {
    if (onEnableWidget) {
      await onEnableWidget(agentId);
      refresh();
      return;
    }
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/chat-widget`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success('Chat widget enabled');
      refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to enable widget');
    }
  };

  const cloneAgent = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/clone`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(`Cloned → ${data.agent?.name || 'copy'}`);
    } catch (err) {
      toast.error(err.message || 'Clone failed');
    }
  };

  const deployAgent = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/deploy`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Deploy failed');
      toast.success('Agent deployed — run mode enabled');
      refresh();
    } catch (err) {
      toast.error(err.message || 'Deploy failed');
    }
  };

  if (!agentId) return null;

  const host = backendUrl || (typeof window !== 'undefined' ? window.location.origin : '');
  const studioChatUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/ai-studio?agent=${agentId}`;

  return (
    <PanelShell
      title="Deploy"
      testId="agent-deploy-panel"
      actions={(
        <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={refresh} disabled={loading}>
          Refresh
        </Button>
      )}
    >
      <div className="flex flex-wrap gap-1 mb-1">
        <Button size="sm" variant="default" className="h-7 text-[10px]" onClick={deployAgent}>
          Deploy
        </Button>
        <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={generateKey}>
          Generate API key
        </Button>
        <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={enableWidget}>
          Enable chat widget
        </Button>
        <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={cloneAgent}>
          Clone agent
        </Button>
      </div>
      {newApiKey?.api_key && (
        <div className="rounded-lg border border-emerald-700/40 bg-emerald-950/20 p-2 mb-1">
          <p className="text-[10px] text-emerald-300 mb-1">API key (copy now — shown once):</p>
          <code className="block text-[10px] text-emerald-100 break-all">{newApiKey.api_key}</code>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 text-[10px] mt-1"
            onClick={() => copyText(newApiKey.api_key, 'API key')}
          >
            Copy key
          </Button>
        </div>
      )}
      {deployments.map((d) => {
        const fullUrl = d.url?.startsWith('http') ? d.url : `${host}${d.url || ''}`;
        const snippet = d.snippet?.replace('<YOUR_BACKEND_URL>', host) || '';
        return (
          <div key={d.type} className="border border-white/5 rounded-lg px-2 py-1.5 space-y-1">
            <div className="font-medium text-zinc-300">{d.label}</div>
            {d.url && (
              <>
                <code className="block text-[10px] text-zinc-500 break-all">{fullUrl}</code>
                <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => copyText(fullUrl, d.label)}>
                  Copy URL
                </Button>
              </>
            )}
            {d.type === 'chat' && (
              <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => copyText(studioChatUrl, 'studio link')}>
                Copy studio link
              </Button>
            )}
            {snippet && (
              <>
                <code className="block text-[10px] text-zinc-600 break-all max-h-16 overflow-y-auto">{snippet}</code>
                <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => copyText(snippet, 'widget snippet')}>
                  Copy snippet
                </Button>
              </>
            )}
            {d.auth && <p className="text-[10px] text-zinc-600">Auth: {d.auth}</p>}
          </div>
        );
      })}
    </PanelShell>
  );
}

export function HandoffChannelsPanel({ agentId, backendUrl, authHeaders, userConnections = [] }) {
  const [handoffEnabled, setHandoffEnabled] = useState(false);
  const [runMode, setRunMode] = useState('run');
  const [channel, setChannel] = useState('web');
  const [slackConnId, setSlackConnId] = useState('');
  const [handoffs, setHandoffs] = useState([]);
  const [saving, setSaving] = useState(false);

  const slackConnections = userConnections.filter(
    (c) => c.status === 'active' && (c.provider_id === 'slack' || c.provider === 'slack'),
  );

  const load = useCallback(() => {
    if (!agentId) return;
    Promise.all([
      fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/instructions`, { headers: authHeaders() }).then((r) => r.json()),
      fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/handoffs`, { headers: authHeaders() }).then((r) => r.json()),
    ])
      .then(([inst, ho]) => {
        setHandoffEnabled(Boolean(inst.handoff_enabled));
        setRunMode(inst.bindings?.run_mode || 'run');
        const ch = inst.channel_config || {};
        setChannel(ch.primary || 'web');
        setSlackConnId(ch.slack_connection_id || '');
        setHandoffs(ho.handoffs || []);
      })
      .catch(() => {});
  }, [agentId, backendUrl, authHeaders]);

  useEffect(load, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/bindings`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          handoff_enabled: handoffEnabled,
          run_mode: runMode,
          channel_config: {
            primary: channel,
            slack_connection_id: channel === 'slack' ? slackConnId : '',
          },
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success('Handoff & channel settings saved');
      load();
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!agentId) return null;

  return (
    <PanelShell
      title="Handoff & channels"
      testId="agent-handoff-panel"
      actions={(
        <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      )}
    >
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={handoffEnabled}
          onChange={(e) => setHandoffEnabled(e.target.checked)}
        />
        <span className="text-zinc-300">Enable human handoff (escalate frustrated users)</span>
      </label>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-zinc-500 block mb-0.5">Run mode</label>
          <select
            value={runMode}
            onChange={(e) => setRunMode(e.target.value)}
            className="w-full h-7 rounded bg-zinc-900 border border-white/10 text-[11px] px-1"
          >
            <option value="run">Run (execute tools)</option>
            <option value="build">Build (plan only)</option>
          </select>
        </div>
        <div>
          <label className="text-zinc-500 block mb-0.5">Primary channel</label>
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            className="w-full h-7 rounded bg-zinc-900 border border-white/10 text-[11px] px-1"
          >
            <option value="web">Web / Studio chat</option>
            <option value="api">REST API</option>
            <option value="slack">Slack</option>
          </select>
        </div>
      </div>
      {channel === 'slack' && (
        <div>
          <label className="text-zinc-500 block mb-0.5">Slack connection</label>
          {slackConnections.length === 0 ? (
            <p className="text-zinc-600">Connect Slack in Connections first.</p>
          ) : (
            <select
              value={slackConnId}
              onChange={(e) => setSlackConnId(e.target.value)}
              className="w-full h-7 rounded bg-zinc-900 border border-white/10 text-[11px] px-1"
            >
              <option value="">Select connection…</option>
              {slackConnections.map((c) => (
                <option key={c.id} value={c.id}>{c.label || c.name || c.id}</option>
              ))}
            </select>
          )}
        </div>
      )}
      {handoffs.length > 0 && (
        <div className="border-t border-white/5 pt-2 mt-1">
          <p className="text-zinc-500 mb-1">Recent handoffs ({handoffs.length})</p>
          {handoffs.slice(0, 5).map((h) => (
            <div key={h.id} className="text-[10px] text-zinc-500 py-0.5">
              <span className="text-zinc-400">{h.status}</span> — {h.reason}
            </div>
          ))}
        </div>
      )}
    </PanelShell>
  );
}

export function AgentStatePanel({ agentId, backendUrl, authHeaders, refreshKey = 0 }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!agentId) return;
    setLoading(true);
    fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/state`, { headers: authHeaders() })
      .then((r) => r.json())
      .then(setState)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [agentId, backendUrl, authHeaders]);

  useEffect(load, [load, refreshKey]);

  if (!agentId) return null;

  const hasValue = state?.value && Object.keys(state.value).length > 0;

  return (
    <PanelShell
      title="Agent state"
      testId="agent-state-panel"
      actions={(
        <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={load} disabled={loading}>
          Refresh
        </Button>
      )}
    >
      {loading && <p className="text-zinc-500">Loading…</p>}
      {!loading && !hasValue && (
        <p className="text-zinc-500">No persistent state yet — run the agent and it will store key-value data here.</p>
      )}
      {!loading && hasValue && (
        <pre className="text-[10px] text-zinc-500 overflow-x-auto max-h-32">{JSON.stringify(state.value, null, 2)}</pre>
      )}
    </PanelShell>
  );
}

export function BrowserLivePanel({ frame, events = [], expanded = false }) {
  const [history, setHistory] = useState([]);
  useEffect(() => {
    if (frame?.screenshot) {
      setHistory((prev) => [...prev.slice(-7), frame]);
    }
  }, [frame?.screenshot, frame?.url]);

  if (!frame?.screenshot && events.length === 0 && history.length === 0) return null;
  const latest = frame?.screenshot ? frame : history[history.length - 1] || {};

  return (
    <PanelShell title="Live browser (Twin Web Agent)" testId="agent-browser-panel">
      <div className={`rounded-lg border border-white/10 bg-black overflow-hidden ${expanded ? 'min-h-[280px]' : ''}`}>
        {latest.url && (
          <div className="px-2 py-1 border-b border-white/10 flex items-center gap-2 bg-zinc-900/80">
            <span className="text-sky-400 shrink-0 text-[10px]" aria-hidden>🌐</span>
            <span className="text-[10px] text-zinc-400 truncate flex-1">{latest.url}</span>
          </div>
        )}
        {latest.screenshot ? (
          <img
            src={`data:image/jpeg;base64,${latest.screenshot}`}
            alt="Browser preview"
            className={`w-full object-contain bg-black ${expanded ? 'max-h-72' : 'max-h-48'}`}
          />
        ) : (
          <p className="text-[10px] text-zinc-600 p-3">Waiting for browser tool…</p>
        )}
      </div>
      {history.length > 1 && (
        <div className="flex gap-1 mt-2 overflow-x-auto">
          {history.map((h, i) => (
            <button
              key={`bh-${i}`}
              type="button"
              className="shrink-0 w-14 h-10 rounded border border-white/10 overflow-hidden opacity-80 hover:opacity-100"
              title={h.url}
            >
              {h.screenshot && (
                <img src={`data:image/jpeg;base64,${h.screenshot}`} alt="" className="w-full h-full object-cover" />
              )}
            </button>
          ))}
        </div>
      )}
      {events.slice(-5).map((evt, i) => (
        <p key={`b-${i}`} className="text-[10px] text-zinc-500 truncate">
          {evt.action || evt.type}: {evt.url || ''}
        </p>
      ))}
    </PanelShell>
  );
}

export function TwinWorkspacePanel({ backendUrl, authHeaders }) {
  const [ws, setWs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${backendUrl}/api/ai-studio/workspace`, { headers: authHeaders() })
      .then((r) => r.json())
      .then(setWs)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [backendUrl, authHeaders]);

  if (loading) return null;
  if (!ws) return null;

  return (
    <PanelShell title="Workspace" testId="twin-workspace-panel">
      <p className="text-[10px] text-zinc-500 mb-2">
        {ws.agents?.length || 0} agents · {ws.triggers?.length || 0} triggers · {ws.credits?.balance ?? '—'} credits
      </p>
      {(ws.recent_runs || []).slice(0, 3).map((r) => (
        <p key={r.id} className="text-[10px] text-zinc-600 truncate">
          Run {String(r.app_id || '').slice(0, 8)}… {(r.output_payload?.output_text || '').slice(0, 60)}
        </p>
      ))}
    </PanelShell>
  );
}

export function TwinCreditsPanel({ backendUrl, authHeaders, credits, onRefresh }) {
  const [granting, setGranting] = useState(false);
  const packs = credits?.packs || [];

  const grant = async (packId) => {
    setGranting(true);
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/credits/grant-dev`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ pack_id: packId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Grant failed');
      toast.success(`+${data.granted} credits (dev grant)`);
      onRefresh?.();
    } catch (e) {
      toast.error(e.message || 'Could not add credits');
    } finally {
      setGranting(false);
    }
  };

  if (!credits?.enabled) return null;

  return (
    <PanelShell title="Agent credits" testId="twin-credits-panel">
      <p className="text-lg font-semibold text-amber-300">{credits.balance ?? 0}</p>
      <p className="text-[10px] text-zinc-500 mb-2">
        Build {credits.build_cost} · Run {credits.run_cost} per step
      </p>
      <div className="flex flex-wrap gap-1">
        {packs.map((p) => (
          <Button
            key={p.id}
            size="sm"
            variant="outline"
            className="h-7 text-[10px] border-amber-500/30 text-amber-200"
            disabled={granting}
            onClick={() => grant(p.id)}
          >
            +{p.credits} (${p.usd})
          </Button>
        ))}
      </div>
      <p className="text-[10px] text-zinc-600 mt-1">Dev grant — production uses Stripe checkout.</p>
    </PanelShell>
  );
}

export function TwinRunModeToggle({ agentId, runMode, onChange, backendUrl, authHeaders }) {
  const save = async (mode) => {
    onChange(mode);
    if (!agentId) return;
    await fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/bindings`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ run_mode: mode }),
    });
    toast.success(mode === 'build' ? 'Build mode — plan only' : 'Run mode — live tools');
  };

  return (
    <div className="inline-flex rounded-lg border border-white/10 overflow-hidden text-[10px]" data-testid="twin-run-mode">
      {['build', 'run'].map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => save(m)}
          className={`px-3 py-1 capitalize ${runMode === m ? 'bg-violet-600 text-white' : 'bg-zinc-900 text-zinc-500 hover:text-zinc-300'}`}
        >
          {m}
        </button>
      ))}
    </div>
  );
}

export function AgentChainPanel({ agentId, backendUrl, authHeaders, allAgents = [] }) {
  const [steps, setSteps] = useState([]);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [runInput, setRunInput] = useState('');
  const [lastResult, setLastResult] = useState(null);

  const load = useCallback(() => {
    if (!agentId) return;
    fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/chain`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => setSteps(d.chain || []))
      .catch(() => {});
  }, [agentId, backendUrl, authHeaders]);

  useEffect(load, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/chain`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ steps }),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success('Chain saved');
      load();
    } catch (err) {
      toast.error(err.message || 'Failed to save chain');
    } finally {
      setSaving(false);
    }
  };

  const addStep = () => {
    const other = allAgents.find((a) => a.id !== agentId);
    setSteps((prev) => [
      ...prev,
      {
        id: `step-${Date.now()}`,
        agent_id: other?.id || '',
        label: other?.name || 'Agent',
        prompt_template: '{{previous_output}}',
      },
    ]);
  };

  const runChain = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/chain/run`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ input: runInput }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Run failed');
      setLastResult(data);
      toast.success('Chain completed');
    } catch (err) {
      toast.error(err.message || 'Chain run failed');
    } finally {
      setRunning(false);
    }
  };

  if (!agentId) return null;

  return (
    <PanelShell
      title="Agent chain"
      testId="agent-chain-panel"
      actions={(
        <div className="flex gap-1">
          <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={addStep}>+ Step</Button>
          <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={save} disabled={saving}>Save</Button>
        </div>
      )}
    >
      <p className="text-[10px] text-zinc-500 mb-2">
        Run agents in sequence — output passes to the next step. Agents can also delegate via chat tools.
      </p>
      {steps.map((step, idx) => (
        <div key={step.id || idx} className="border border-white/5 rounded-lg p-2 space-y-1 mb-1">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-violet-400">#{idx + 1}</span>
            <select
              value={step.agent_id}
              onChange={(e) => {
                const id = e.target.value;
                const ag = allAgents.find((a) => a.id === id);
                setSteps((prev) => prev.map((s, i) => (
                  i === idx ? { ...s, agent_id: id, label: ag?.name || '' } : s
                )));
              }}
              className="flex-1 bg-zinc-900 border border-zinc-700 rounded px-1 py-0.5 text-[10px]"
            >
              <option value="">Select agent…</option>
              {allAgents.filter((a) => a.id !== agentId).map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 text-[10px] text-red-400"
              onClick={() => setSteps((prev) => prev.filter((_, i) => i !== idx))}
            >
              Remove
            </Button>
          </div>
          <input
            value={step.prompt_template || ''}
            onChange={(e) => setSteps((prev) => prev.map((s, i) => (
              i === idx ? { ...s, prompt_template: e.target.value } : s
            )))}
            placeholder="Prompt template — use {{previous_output}} or {{input}}"
            className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-[10px]"
          />
        </div>
      ))}
      {steps.length === 0 && (
        <p className="text-zinc-500 text-[11px]">No chain steps — add agents to orchestrate multi-step workflows.</p>
      )}
      <div className="flex gap-1 mt-2">
        <input
          value={runInput}
          onChange={(e) => setRunInput(e.target.value)}
          placeholder="Initial input for chain…"
          className="flex-1 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-[10px]"
        />
        <Button size="sm" className="h-7 text-[10px]" onClick={runChain} disabled={running || !steps.length}>
          Run chain
        </Button>
      </div>
      {lastResult?.output_text && (
        <p className="text-[10px] text-zinc-400 mt-2 line-clamp-4 whitespace-pre-wrap">{lastResult.output_text}</p>
      )}
    </PanelShell>
  );
}

export function AgentSetupGrid({
  agentId,
  backendUrl,
  authHeaders,
  knowledgeBases,
  onReloadKnowledgeBases,
  userConnections,
  onGenerateApiKey,
  onEnableWidget,
  stateRefreshKey = 0,
  allAgents = [],
  browserFrame = null,
  browserEvents = [],
  browserExpanded = false,
}) {
  if (!agentId) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="agent-setup-grid">
      <InstructionsPanel agentId={agentId} backendUrl={backendUrl} authHeaders={authHeaders} />
      <KnowledgeAttachPanel
        agentId={agentId}
        backendUrl={backendUrl}
        authHeaders={authHeaders}
        knowledgeBases={knowledgeBases}
        onReloadKnowledgeBases={onReloadKnowledgeBases}
      />
      <McpToolsPanel agentId={agentId} backendUrl={backendUrl} authHeaders={authHeaders} />
      <TriggersPanel agentId={agentId} backendUrl={backendUrl} authHeaders={authHeaders} />
      <AgentChainPanel
        agentId={agentId}
        backendUrl={backendUrl}
        authHeaders={authHeaders}
        allAgents={allAgents}
      />
      <BrowserLivePanel frame={browserFrame} events={browserEvents} expanded={browserExpanded} />
      <DeployPanel
        agentId={agentId}
        backendUrl={backendUrl}
        authHeaders={authHeaders}
        onGenerateApiKey={onGenerateApiKey}
        onEnableWidget={onEnableWidget}
      />
      <HandoffChannelsPanel
        agentId={agentId}
        backendUrl={backendUrl}
        authHeaders={authHeaders}
        userConnections={userConnections}
      />
      <AgentStatePanel
        agentId={agentId}
        backendUrl={backendUrl}
        authHeaders={authHeaders}
        refreshKey={stateRefreshKey}
      />
    </div>
  );
}

export function CommunityTemplatesPanel({ backendUrl, authHeaders, onCloned }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${backendUrl}/api/ai-studio/templates/community`, { headers: authHeaders() })
      .then(async (r) => {
        if (!r.ok) return { templates: [] };
        return r.json();
      })
      .then((d) => {
        if (!cancelled) setTemplates(d.templates || []);
      })
      .catch(() => {
        /* optional panel — don't toast over the main catalog */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // authHeaders is recreated each render — depend on backendUrl only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendUrl]);

  const clone = async (tpl) => {
    const res = await fetch(`${backendUrl}/api/ai-studio/apps/from-community-template`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ template_id: tpl.id }),
    });
    const data = await res.json();
    if (!res.ok) {
      toast.error(data.detail || 'Clone failed');
      return;
    }
    toast.success(`Created ${data.agent?.name || 'agent'}`);
    onCloned?.(data);
  };

  return (
    <div className="space-y-3 mt-6 border-t border-white/10 pt-4" data-testid="community-templates-panel">
      <div>
        <h3 className="text-sm font-semibold text-zinc-200">Community recipe templates</h3>
        <p className="text-[11px] text-zinc-500 mt-0.5">
          Smaller set of ready-made automation recipes (separate from the expert agency catalog above).
        </p>
      </div>
      {loading && <p className="text-xs text-zinc-500">Loading community recipes…</p>}
      {!loading && templates.length === 0 && (
        <p className="text-xs text-zinc-500">No community templates available.</p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
        {templates.map((tpl) => (
          <button
            key={tpl.id}
            type="button"
            onClick={() => clone(tpl)}
            className="glass rounded-xl p-3 text-left hover:border-violet-500/40 border border-white/5"
          >
            <div className="font-semibold text-zinc-200">{tpl.name}</div>
            <div className="text-zinc-500 mt-1">{tpl.description}</div>
            {tpl.tools?.length > 0 && (
              <div className="text-[10px] text-violet-400 mt-2">{tpl.tools.join(' · ')}</div>
            )}
          </button>
        ))}
      </div>
      {!loading && templates.length > 0 ? (
        <p className="text-[10px] text-zinc-600">{templates.length} community recipe{templates.length === 1 ? '' : 's'}.</p>
      ) : null}
    </div>
  );
}
